import React, { useState } from 'react';
import {
  Card,
  Select,
  Button,
  Table,
  Tag,
  message,
  Spin,
  Space,
  Input,
  Modal,
  Avatar,
  Collapse,
  Tooltip,
  Alert
} from 'antd';
import {
  SyncOutlined,
  SearchOutlined,
  LinkOutlined,
  PlusOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloudDownloadOutlined,
  GlobalOutlined,
  UserOutlined
} from '@ant-design/icons';
import { fetchProfiles, getProfileById } from '../../server/profiles/profilesServer';
import { rescrapeProfile, updateProfileScrapSource, fetchTixrGroupEvents, detectSourceType } from '../../server/scraper/raScraperServer';

const { Option } = Select;

const EventSyncTab = ({ countries = [], onScrapeEvent }) => {
  // City filter state
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [cities, setCities] = useState([]);

  // Profiles state
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  // Sync state
  const [syncingProfileId, setSyncingProfileId] = useState(null);
  const [syncResults, setSyncResults] = useState({}); // keyed by profileId
  const [expandedRows, setExpandedRows] = useState([]);

  // ScrapSource edit state
  const [editingProfileId, setEditingProfileId] = useState(null);
  const [editSourceUrl, setEditSourceUrl] = useState('');
  const [savingSource, setSavingSource] = useState(false);

  // Import state
  const [importingEventUrl, setImportingEventUrl] = useState(null);

  // Only countries that have at least one enabled city
  const activeCountries = countries.filter(c =>
    c.cities?.some(city => city.isEnabled)
  );

  const handleCountryChange = (countryId) => {
    setSelectedCountryId(countryId);
    setSelectedCityId(null);
    setProfiles([]);
    setSyncResults({});
    const country = countries.find(c => c._id === countryId);
    // Only show enabled cities
    setCities((country?.cities || []).filter(city => city.isEnabled));
  };

  const handleLoadProfiles = async () => {
    if (!selectedCityId) {
      message.warning('Please select a city');
      return;
    }

    try {
      setLoadingProfiles(true);
      setSyncResults({});

      // Use fetchProfiles with EM filter (server-side profileType filter works here)
      const result = await fetchProfiles(true, 1, 500, true, undefined, undefined, 'EM');
      const allProfiles = result?.data?.data || result?.data || [];

      // Filter by selected city client-side
      const emProfiles = allProfiles.filter(p => {
        const profileCityId = typeof p.city === 'object' ? p.city?._id : p.city;
        return profileCityId === selectedCityId;
      });

      // Enrich profiles with scrapSource (fetch full profile data in parallel)
      const enriched = await Promise.all(
        emProfiles.map(async (p) => {
          try {
            const full = await getProfileById(p._id);
            return { ...p, scrapSource: full?.scrapSource || null };
          } catch {
            return p;
          }
        })
      );

      setProfiles(enriched);
      message.success(`Found ${enriched.length} venue profiles`);
    } catch (error) {
      message.error('Failed to load profiles');
      console.error(error);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const handleSync = async (profile) => {
    const sourceType = detectSourceType(profile.scrapSource);

    try {
      setSyncingProfileId(profile._id);

      let result;
      if (sourceType === 'tixr') {
        // Tixr: fetch group events directly from worker
        const tixrData = await fetchTixrGroupEvents(profile.scrapSource);
        result = {
          totalFromSource: tixrData.totalEvents || tixrData.events?.length || 0,
          existingCount: 0,
          missingCount: tixrData.totalEvents || tixrData.events?.length || 0,
          existing: [],
          missing: (tixrData.events || []).map(e => ({
            ...e,
            raEventId: e.url,
            status: 'missing'
          })),
          message: `Found ${tixrData.totalEvents || 0} events from Tixr`
        };
      } else {
        // RA: use backend rescrape-profile endpoint
        result = await rescrapeProfile(profile._id);
      }

      setSyncResults(prev => ({ ...prev, [profile._id]: result }));
      // Auto-expand the row to show results
      setExpandedRows(prev => prev.includes(profile._id) ? prev : [...prev, profile._id]);
      message.success(`${result.message || `Found ${result.totalFromSource} events`}`);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Sync failed';
      message.error(errorMsg);
    } finally {
      setSyncingProfileId(null);
    }
  };

  const handleSaveSource = async (profileId) => {
    if (!editSourceUrl.trim()) {
      message.warning('Please enter a valid RA club URL');
      return;
    }

    try {
      setSavingSource(true);
      await updateProfileScrapSource(profileId, editSourceUrl.trim());
      // Update local state
      setProfiles(prev => prev.map(p =>
        p._id === profileId ? { ...p, scrapSource: editSourceUrl.trim() } : p
      ));
      setEditingProfileId(null);
      setEditSourceUrl('');
      message.success('Scrap source updated');
    } catch (error) {
      message.error('Failed to update scrap source');
    } finally {
      setSavingSource(false);
    }
  };

  const handleImportEvent = async (event) => {
    if (!onScrapeEvent) return;
    try {
      setImportingEventUrl(event.url);
      const sourceType = detectSourceType(event.url);
      await onScrapeEvent(event.url, { raEventId: event.raEventId, sourceType });
      message.success(`Event "${event.title}" ready for creation`);
    } catch (error) {
      message.error('Failed to import event');
    } finally {
      setImportingEventUrl(null);
    }
  };

  // Profile table columns
  const profileColumns = [
    {
      title: 'Profile',
      key: 'profile',
      width: 250,
      render: (_, record) => (
        <Space>
          <Avatar src={record.picture} icon={<UserOutlined />} size={40} />
          <div>
            <div className="font-medium">{record.username}</div>
            <div className="text-xs text-gray-400">{record.profileType}</div>
          </div>
        </Space>
      )
    },
    {
      title: 'Scrap Source',
      key: 'scrapSource',
      ellipsis: true,
      render: (_, record) => {
        if (editingProfileId === record._id) {
          return (
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="https://ra.co/clubs/..."
                value={editSourceUrl}
                onChange={(e) => setEditSourceUrl(e.target.value)}
                onPressEnter={() => handleSaveSource(record._id)}
              />
              <Button type="primary" loading={savingSource} onClick={() => handleSaveSource(record._id)}>
                Save
              </Button>
              <Button onClick={() => { setEditingProfileId(null); setEditSourceUrl(''); }}>
                Cancel
              </Button>
            </Space.Compact>
          );
        }

        if (record.scrapSource) {
          const type = detectSourceType(record.scrapSource);
          return (
            <Space>
              <Tag color={type === 'ra' ? 'purple' : type === 'tixr' ? 'blue' : 'default'}>
                {type.toUpperCase()}
              </Tag>
              <a href={record.scrapSource} target="_blank" rel="noreferrer" className="text-blue-500 text-xs">
                {record.scrapSource}
              </a>
              <Tooltip title="Edit source">
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  type="text"
                  onClick={() => {
                    setEditingProfileId(record._id);
                    setEditSourceUrl(record.scrapSource);
                  }}
                />
              </Tooltip>
            </Space>
          );
        }
        return <Tag color="default">Not set</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          {record.scrapSource ? (
            <Button
              type="primary"
              icon={<SyncOutlined spin={syncingProfileId === record._id} />}
              loading={syncingProfileId === record._id}
              onClick={() => handleSync(record)}
            >
              Sync
            </Button>
          ) : (
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingProfileId(record._id);
                setEditSourceUrl('');
              }}
            >
              Set Source
            </Button>
          )}
        </Space>
      )
    },
  ];

  // Render sync results for a profile
  const renderSyncResults = (profileId) => {
    const result = syncResults[profileId];
    if (!result) return null;

    return (
      <div className="p-4 bg-gray-50 border-t">
        {/* Summary */}
        <Alert
          message={`Found ${result.totalFromSource} events: ${result.existingCount} in DB, ${result.missingCount} new`}
          type="info"
          showIcon
          className="mb-4"
        />

        {/* Existing events */}
        {result.existing?.length > 0 && (
          <Collapse ghost className="mb-2">
            <Collapse.Panel
              header={
                <span>
                  <CheckCircleOutlined className="text-green-500 mr-2" />
                  Already in DB ({result.existingCount})
                </span>
              }
              key="existing"
            >
              <div className="space-y-2">
                {result.existing.map((event) => (
                  <div key={event.raEventId} className="flex items-center justify-between p-2 bg-green-50 rounded">
                    <div>
                      <span className="font-medium">{event.raTitle || event.dbTitle}</span>
                      <span className="text-gray-400 text-xs ml-2">{event.raDate?.split('T')[0]}</span>
                    </div>
                    <Tag color="success">In DB</Tag>
                  </div>
                ))}
              </div>
            </Collapse.Panel>
          </Collapse>
        )}

        {/* Missing events */}
        {result.missing?.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <ExclamationCircleOutlined className="text-orange-500" />
              <span className="font-medium">Missing / New Events ({result.missingCount})</span>
            </div>
            <div className="space-y-2">
              {result.missing.map((event) => (
                <div key={event.raEventId} className="flex items-center justify-between p-3 bg-orange-50 rounded border border-orange-100">
                  <div className="flex items-center gap-3">
                    {event.image && (
                      <img src={event.image} alt="" className="w-12 h-12 object-cover rounded" />
                    )}
                    <div>
                      <div className="font-medium">{event.title}</div>
                      <div className="text-xs text-gray-500">
                        {event.date?.split('T')[0]}
                        {event.artists?.length > 0 && ` - ${event.artists.join(', ')}`}
                      </div>
                    </div>
                  </div>
                  <Space>
                    <Tooltip title="View on RA">
                      <Button
                        icon={<LinkOutlined />}
                        size="small"
                        onClick={() => window.open(event.url, '_blank')}
                      />
                    </Tooltip>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      size="small"
                      loading={importingEventUrl === event.url}
                      onClick={() => handleImportEvent(event)}
                    >
                      Import
                    </Button>
                  </Space>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.totalFromSource === 0 && (
          <Alert message="No events found from this source" type="warning" showIcon />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* City Filter */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <SyncOutlined className="text-blue-500" />
            <span>Event Sync</span>
          </div>
        }
        className="shadow-lg"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <Select
              value={selectedCountryId}
              onChange={handleCountryChange}
              style={{ width: '100%' }}
              size="large"
              placeholder="Select a country"
              showSearch
              optionFilterProp="children"
            >
              {activeCountries.map(country => (
                <Option key={country._id} value={country._id}>
                  {country.name}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <Select
              value={selectedCityId}
              onChange={setSelectedCityId}
              style={{ width: '100%' }}
              size="large"
              placeholder="Select a city"
              disabled={!selectedCountryId}
              showSearch
              optionFilterProp="children"
            >
              {cities.map(city => (
                <Option key={city._id} value={city._id}>
                  {city.name}
                </Option>
              ))}
            </Select>
          </div>

          <div className="flex items-end">
            <Button
              type="primary"
              icon={<CloudDownloadOutlined />}
              size="large"
              loading={loadingProfiles}
              onClick={handleLoadProfiles}
              disabled={!selectedCityId}
            >
              Load Profiles
            </Button>
          </div>
        </div>
      </Card>

      {/* Profiles Table with expandable sync results */}
      {profiles.length > 0 && (
        <Card
          title={
            <div className="flex items-center gap-2">
              <GlobalOutlined className="text-green-500" />
              <span>Venue Profiles ({profiles.length})</span>
            </div>
          }
          className="shadow-lg"
        >
          <Table
            columns={profileColumns}
            dataSource={profiles}
            rowKey="_id"
            pagination={{ pageSize: 50 }}
            size="middle"
            expandable={{
              expandedRowRender: (record) => renderSyncResults(record._id),
              rowExpandable: (record) => !!syncResults[record._id],
              expandedRowKeys: expandedRows,
              onExpandedRowsChange: (keys) => setExpandedRows(keys),
            }}
          />
        </Card>
      )}

      {/* Empty state */}
      {!loadingProfiles && profiles.length === 0 && selectedCityId && (
        <Card className="shadow-lg">
          <Alert
            message="No Profiles"
            description="No venue profiles found for this city. Click 'Load Profiles' to search."
            type="info"
            showIcon
          />
        </Card>
      )}
    </div>
  );
};

export default EventSyncTab;
